import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Download, Mail, Disc, Music2, Search, RefreshCw, Package, Clock } from "lucide-react";
import { toast } from "sonner";
import { getFileUrl } from "@/components/FileUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function MyDownloads() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState({});

  // Persist last email in localStorage
  useEffect(() => {
    const stored = localStorage.getItem("lx6_customer_email");
    if (stored) setEmail(stored);
  }, []);

  const lookup = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const clean = (email || "").trim().toLowerCase();
    if (!clean || !clean.includes("@")) return toast.error("Enter a valid email");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders/lookup`, { email: clean });
      localStorage.setItem("lx6_customer_email", clean);
      setOrders(data.orders || []);
      if ((data.orders || []).length === 0) toast.info("No purchases found for that email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async (orderId) => {
    setResending((r) => ({ ...r, [orderId]: true }));
    try {
      await axios.post(`${API}/orders/${orderId}/resend`, { email: email.trim().toLowerCase() });
      toast.success("Fresh download link sent to your email");
      await lookup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    } finally {
      setResending((r) => ({ ...r, [orderId]: false }));
    }
  };

  return (
    <div data-testid="my-downloads-page" className="pt-24 min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.5em] font-semibold mb-3">Download Manager</p>
          <h1 className="font-heading text-5xl sm:text-6xl leading-[0.95] tracking-wide">MY DOWNLOADS</h1>
          <p className="text-white/45 mt-3 max-w-lg">
            Re-download your purchases anytime. Enter the email you used at checkout.
          </p>
        </div>

        <form onSubmit={lookup} className="flex gap-2 mb-10 max-w-lg">
          <div className="relative flex-1">
            <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="downloads-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-3 py-3 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/25"
            />
          </div>
          <Button
            data-testid="downloads-lookup-btn"
            type="submit"
            disabled={loading}
            className="bg-[#D4AF37] text-black hover:bg-white font-semibold uppercase tracking-wider text-xs px-6"
          >
            {loading ? "Looking…" : (<><Search className="w-4 h-4 mr-1.5" />Find</>)}
          </Button>
        </form>

        {orders === null ? (
          <div className="text-center py-16 text-white/25">
            <Package className="w-10 h-10 mx-auto mb-4 opacity-40" />
            <p className="text-sm">Enter your email above to see your library.</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <p className="text-lg">No purchases found for {email}.</p>
            <p className="text-sm mt-2 text-white/20">Contact support if you think this is a mistake.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-4">{orders.length} purchase{orders.length===1?"":"s"}</p>
            {orders.map((o) => {
              const isZip = o.download_kind === "zip";
              const attemptsLeft = Math.max(0, (o.download_max_attempts || 5) - (o.download_attempts || 0));
              const cover = o.cover_art ? getFileUrl(o.cover_art) : "";
              return (
                <div
                  key={o.order_id}
                  data-testid={`downloads-order-${o.order_id}`}
                  className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4 sm:p-5 flex flex-col sm:flex-row gap-5 hover:border-white/10 transition-colors"
                >
                  <div className="w-full sm:w-20 h-20 bg-[#111] rounded flex-shrink-0 overflow-hidden">
                    {cover ? (
                      <img src={cover} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {isZip ? <Disc className="w-6 h-6 text-white/20" /> : <Music2 className="w-6 h-6 text-white/20" />}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-lg font-semibold">{o.product_title}</p>
                      {isZip && <span className="text-[9px] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-1.5 py-0.5 rounded font-bold">ZIP · {o.product_type?.toUpperCase()}</span>}
                    </div>
                    <p className="text-xs text-white/40 mt-1 flex items-center gap-3 flex-wrap">
                      <span>{formatDate(o.paid_at)}</span>
                      <span>·</span>
                      <span>${(o.amount || 0).toFixed(2)}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {attemptsLeft} of {o.download_max_attempts || 5} downloads left</span>
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:min-w-[220px]">
                    {o.download_granted && attemptsLeft > 0 ? (
                      <a
                        href={o.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`downloads-dl-${o.order_id}`}
                        className="inline-flex items-center justify-center gap-2 h-11 bg-[#D4AF37] text-black hover:bg-white transition-colors text-xs uppercase tracking-wider font-semibold rounded px-4"
                      >
                        <Download className="w-4 h-4" /> Download {isZip ? "ZIP" : "MP3"}
                      </a>
                    ) : null}
                    {o.download_url_wav && attemptsLeft > 0 && (
                      <a
                        href={o.download_url_wav}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`downloads-dl-wav-${o.order_id}`}
                        className="inline-flex items-center justify-center gap-2 h-10 border border-white/10 text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-[11px] uppercase tracking-wider font-semibold rounded px-3"
                      >
                        <Download className="w-3.5 h-3.5" /> WAV (Lossless)
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resend(o.order_id)}
                      disabled={resending[o.order_id]}
                      data-testid={`downloads-resend-${o.order_id}`}
                      className="text-white/40 hover:text-white text-[11px] uppercase tracking-wider h-8"
                    >
                      <RefreshCw className={`w-3 h-3 mr-1.5 ${resending[o.order_id] ? "animate-spin" : ""}`} />
                      Email me a fresh link
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
